import React, { useCallback, useEffect, useMemo } from 'react';
import { Symbol, filterSymbols } from '../../hooks/useSymbols';
import { useUI } from '../../contexts';

type GoToSymbolModalProps = {
  symbols: Symbol[];
  onSelect: (symbol: Symbol) => void;
  onClose: () => void;
};

export const GoToSymbolModal = React.memo(function GoToSymbolModal({
  symbols,
  onSelect,
  onClose,
}: GoToSymbolModalProps) {
  const {
    symbolQuery,
    setSymbolQuery,
    symbolHighlight,
    setSymbolHighlight,
    symbolInputRef,
  } = useUI();

  const filteredSymbols = useMemo(
    () => filterSymbols(symbols, symbolQuery),
    [symbols, symbolQuery]
  );

  // Reset highlight when results change
  useEffect(() => {
    if (symbolHighlight >= filteredSymbols.length) {
      setSymbolHighlight(Math.max(0, filteredSymbols.length - 1));
    }
  }, [filteredSymbols.length, symbolHighlight, setSymbolHighlight]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSymbolHighlight(Math.min(symbolHighlight + 1, filteredSymbols.length - 1));
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSymbolHighlight(Math.max(symbolHighlight - 1, 0));
        return;
      }

      if (e.key === 'Enter' && filteredSymbols.length > 0) {
        e.preventDefault();
        const symbol = filteredSymbols[symbolHighlight];
        if (symbol) {
          onSelect(symbol);
        }
        return;
      }
    },
    [filteredSymbols, symbolHighlight, setSymbolHighlight, onSelect, onClose]
  );

  const getKindColor = (kind: Symbol['kind']) => {
    switch (kind) {
      case 'function':
        return 'var(--accent)';
      case 'class':
        return '#f59e0b';
      case 'interface':
        return '#22c55e';
      case 'type':
        return '#a855f7';
      case 'enum':
        return '#ef4444';
      default:
        return 'var(--muted)';
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <div className="goto-modal symbol-modal" onKeyDown={handleKeyDown}>
        <div className="goto-header">
          <span className="goto-icon">@</span>
          <input
            ref={symbolInputRef}
            type="text"
            className="goto-input"
            placeholder="Go to symbol... (@:kind to filter)"
            value={symbolQuery}
            onChange={(e) => {
              setSymbolQuery(e.target.value);
              setSymbolHighlight(0);
            }}
            autoFocus
          />
          <span className="goto-hint">{filteredSymbols.length} symbols</span>
        </div>

        <div className="goto-results">
          {filteredSymbols.length === 0 ? (
            <div className="goto-empty">
              {symbols.length === 0 ? 'No symbols found in this file' : 'No matching symbols'}
            </div>
          ) : (
            filteredSymbols.map((symbol, index) => (
              <button
                key={`${symbol.name}-${symbol.line}`}
                className={`goto-item symbol-item ${index === symbolHighlight ? 'highlighted' : ''}`}
                onClick={() => onSelect(symbol)}
                onMouseEnter={() => setSymbolHighlight(index)}
              >
                <span
                  className="symbol-icon"
                  style={{ color: getKindColor(symbol.kind) }}
                >
                  {symbol.icon}
                </span>
                <span className="symbol-name">{symbol.name}</span>
                <span className="symbol-kind">{symbol.kind}</span>
                <span className="symbol-line">:{symbol.line}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </>
  );
});
